import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import { api } from "../lib/api";
import { useMeta, injectJsonLd, removeJsonLd } from "../lib/seo";
import { articleJsonLd, breadcrumbJsonLd } from "../seoShared";
import { track } from "../lib/analytics";
import { Button } from "../components/ui/button";

const inline = (s) => s
  .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
  .replace(/\*([^*]+)\*/g, "<em>$1</em>")
  .replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, '<a href="$2" class="text-emerald-700 font-medium hover:underline">$1</a>');

const safeInline = (s) => DOMPurify.sanitize(inline(s), { ALLOWED_TAGS: ["strong", "em", "a"], ALLOWED_ATTR: ["href", "class"] });

function renderMarkdown(content, midCta = null) {
  const out = [];
  let heads = 0;
  content.split("\n").forEach((line, i) => {
    if (line.startsWith("## ")) { heads++; out.push(<h2 key={i} className="text-2xl font-bold mt-8 mb-3">{line.slice(3)}</h2>); }
    else if (line.startsWith("# ")) { heads++; out.push(<h2 key={i} className="text-2xl font-bold mt-8 mb-3">{line.slice(2)}</h2>); }
    else if (line.startsWith("- ")) out.push(<li key={i} className="text-base text-slate-700 ml-5 list-disc" dangerouslySetInnerHTML={{ __html: safeInline(line.slice(2)) }} />);
    else if (line.trim()) out.push(<p key={i} className="text-base leading-relaxed text-slate-700 mt-4" dangerouslySetInnerHTML={{ __html: safeInline(line) }} />);
    if (heads === 3 && midCta) { out.push(<div key="mid-cta">{midCta}</div>); heads = -999; }
  });
  return out;
}

// Posts about a specific denial reason get a reason-matched mid-CTA instead of the generic one.
const MID_CTA_REASON = {
  "what-pre-existing-condition-actually-has-to-mean-in-a-contract": { en: "pre-existing condition", es: "condición preexistente" },
};

export function BlogList() {
  useMeta("Denied Claim Blog: Warranty and Service Contract Appeals", "Articles on denied warranty and service contract claims: common denial reasons, contract terms, and what to do after a denial.");
  const [posts, setPosts] = useState([]);
  const [types, setTypes] = useState([]);
  const [filter, setFilter] = useState("all");
  useEffect(() => {
    api.get("/blog/posts").then((r) => setPosts(r.data)).catch(() => {});
    api.get("/claim-types").then((r) => setTypes(r.data)).catch(() => {});
    if (!document.head.querySelector('link[rel="alternate"][type="application/rss+xml"]')) {
      const el = document.createElement("link");
      el.rel = "alternate";
      el.type = "application/rss+xml";
      el.title = "Denial Decoder Blog RSS";
      el.href = "/api/blog/rss.xml";
      document.head.appendChild(el);
    }
  }, []);
  const shown = filter === "all" ? posts
    : posts.filter((p) => p.claim_type === filter || p.claim_type === "cross-type");
  const usedTypes = [...new Set(posts.map((p) => p.claim_type).filter(Boolean))];
  return (
    <div className="max-w-3xl mx-auto px-4 py-16 fade-in" data-testid="blog-list-page">
      <h1 className="text-4xl font-extrabold tracking-tight mb-2">The Decode Blog</h1>
      <p className="text-slate-600 mb-6">Practical guides for fighting denied claims. <a href="/api/blog/rss.xml" className="text-emerald-700 font-medium hover:underline" data-testid="blog-rss-link">RSS</a></p>
      {usedTypes.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-8" data-testid="blog-type-filter">
          <button onClick={() => setFilter("all")} data-testid="blog-filter-all"
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${filter === "all" ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-300 hover:border-emerald-400"}`}>All</button>
          {usedTypes.map((ct) => (
            <button key={ct} onClick={() => setFilter(ct)} data-testid={`blog-filter-${ct}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${filter === ct ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-300 hover:border-emerald-400"}`}>
              {types.find((t) => t.slug === ct)?.name || (ct === "cross-type" ? "Cross-type" : ct)}
            </button>
          ))}
        </div>
      )}
      <div className="space-y-6">
        {shown.map((p) => (
          <Link key={p.slug} to={`/blog/${p.slug}`} className="block bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-md transition-shadow" data-testid={`blog-post-${p.slug}`}>
            {p.claim_type && <span className="text-xs font-bold bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full" data-testid={`blog-tag-${p.slug}`}>{types.find((t) => t.slug === p.claim_type)?.name || (p.claim_type === "cross-type" ? "Cross-type" : p.category || p.claim_type)}</span>}
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-2">{new Date(p.date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</p>
            <h2 className="text-xl font-semibold mt-1">{p.title}</h2>
            <p className="text-sm text-slate-500 mt-2">{p.excerpt}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [missing, setMissing] = useState(false);
  const [related, setRelated] = useState([]);
  const { t, i18n } = useTranslation();
  const isEs = (i18n.language || "").startsWith("es");
  const es = isEs ? post?.es : null;
  const midReason = MID_CTA_REASON[slug];
  const midCta = (
    <div className="my-8 bg-emerald-50 border border-emerald-200 rounded-2xl p-5" data-testid="blog-mid-cta">
      <p className="font-semibold text-emerald-900">
        {midReason ? t("blog.midReasonTitle", { reason: isEs ? midReason.es : midReason.en }) : t("blog.midTitle")}
      </p>
      <p className="text-sm text-emerald-900/80 mt-1">
        {midReason ? t("blog.midReasonBody") : t("blog.midBody")}
      </p>
      <Button onClick={() => { track("cta_click", { location: "blog_mid", slug }); navigate("/check"); }} size="sm" className="mt-3 bg-emerald-700 hover:bg-emerald-800 rounded-full" data-testid="blog-mid-cta-button">Decode my denial — free</Button>
    </div>
  );
  useMeta(
    post ? (post.meta_title || `${post.title} | Denial Decoder`) : "Blog | Denial Decoder",
    post ? (post.meta_description || post.excerpt) : undefined,
    post ? { ogType: "article", image: post.image_url } : undefined);

  useEffect(() => {
    api.get(`/blog/posts/${slug}`).then((r) => {
      setPost(r.data);
      if (r.data.claim_type) {
        api.get(`/blog/posts?claim_type=${r.data.claim_type}`).then((l) =>
          setRelated((l.data || []).filter((x) => x.slug !== slug).slice(0, 3))).catch(() => {});
      }
    }).catch(() => setMissing(true));
  }, [slug]);

  useEffect(() => {
    if (!post) return;
    injectJsonLd("article-schema", articleJsonLd({ ...post, slug }));
    injectJsonLd("breadcrumb-schema", breadcrumbJsonLd(`blog/${slug}`, post.title, "blog", "Blog"));
    return () => { removeJsonLd("article-schema"); removeJsonLd("breadcrumb-schema"); };
  }, [post]);

  if (missing) return <div className="max-w-3xl mx-auto px-4 py-24 text-center"><h1 className="text-2xl font-bold">Post not found</h1></div>;
  if (!post) return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-slate-500">Loading…</div>;
  return (
    <div className="max-w-3xl mx-auto px-4 py-16 fade-in" data-testid="blog-post-page">
      <nav aria-label="Breadcrumb" className="text-sm text-slate-500 mb-6 flex items-center gap-2 flex-wrap" data-testid="breadcrumb">
        <Link to="/" className="hover:text-emerald-700">Home</Link>
        <span aria-hidden="true">/</span>
        <Link to="/blog" className="hover:text-emerald-700">Blog</Link>
        <span aria-hidden="true">/</span>
        <span className="text-slate-700">{es?.title || post.title}</span>
      </nav>
      {post.claim_type && <span className="text-xs font-bold bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full" data-testid="blog-post-tag">{post.claim_type === "cross-type" ? "Cross-type" : post.claim_type}</span>}
      <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700 mt-3 mb-2">{new Date(post.date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</p>
      <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-8">{es?.title || post.title}</h1>
      {post.image_url && <img src={post.image_url.replace("https://decodemydenial.com", "https://www.decodemydenial.com")} alt={post.image_alt || post.title} fetchPriority="high" className="rounded-2xl mb-8 w-full object-cover max-h-80" data-testid="blog-featured-image" />}
      <article>{renderMarkdown(es?.content || post.content, midCta)}</article>
      {related.length > 0 && (
        <div className="mt-12 border-t border-slate-200 pt-8" data-testid="blog-related">
          <h2 className="text-lg font-bold mb-4">Related reading</h2>
          <div className="space-y-3">
            {related.map((r) => (
              <Link key={r.slug} to={`/blog/${r.slug}`} className="block text-emerald-700 font-medium hover:underline" data-testid={`blog-related-${r.slug}`}>{r.title}</Link>
            ))}
          </div>
        </div>
      )}
      <div className="mt-12 bg-emerald-50 border border-emerald-200 rounded-2xl p-6 text-center">
        <p className="font-semibold text-emerald-900 mb-4">Dealing with a denial right now?</p>
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <Button onClick={() => { track("cta_click", { location: "blog_bottom", slug }); navigate("/check"); }} className="bg-emerald-700 hover:bg-emerald-800 rounded-full" data-testid="blog-cta">Decode my denial — free</Button>
          <Button variant="outline" onClick={() => navigate("/guide")} className="rounded-full" data-testid="blog-guide-cta">Read the free guide</Button>
        </div>
      </div>
    </div>
  );
}
